import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RichTextEditor } from './RichTextEditor';
import { NextStepsEditor } from './NextStepsEditor';
import { MeetingBlock } from './MeetingBlock';
import { leadMeetingsAPI } from '../lib/api';

export const MeetingEditor = ({ meeting, ceos, onBack, onSaved, onDeleted }) => {
  const [m, setM] = useState(meeting);
  const [status, setStatus] = useState('saved');
  const pending = useRef({});
  const timer = useRef(null);

  const flush = async () => {
    const data = pending.current;
    pending.current = {};
    if (!Object.keys(data).length) return;
    setStatus('saving');
    try {
      const saved = await leadMeetingsAPI.update(m.id, data);
      onSaved(saved);
      setStatus('saved');
    } catch (e) { setStatus('error'); toast.error(e.message); }
  };

  const change = (patch) => {
    setM((prev) => ({ ...prev, ...patch }));
    pending.current = { ...pending.current, ...patch };
    setStatus('dirty');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 1000);
  };

  useEffect(() => () => { clearTimeout(timer.current); flush(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCeo = (id) => change({ ceo_ids: m.ceo_ids.includes(id) ? m.ceo_ids.filter((x) => x !== id) : [...m.ceo_ids, id] });

  const remove = async () => {
    if (!window.confirm('¿Eliminar esta reunión? No se puede deshacer.')) return;
    pending.current = {};
    clearTimeout(timer.current);
    await leadMeetingsAPI.remove(m.id).then(() => { toast.success('Reunión eliminada'); onDeleted(m.id); }).catch((e) => toast.error(e.message));
  };

  return (
    <div className="space-y-5" data-testid="meeting-editor">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="meeting-editor-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Reuniones</Button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="meeting-save-status">
            {status === 'saving' || status === 'dirty' ? <><Loader2 className="w-3 h-3 animate-spin" />Guardando…</> : status === 'error' ? 'Error al guardar' : <><Check className="w-3 h-3" />Guardado</>}
          </span>
          <Button variant="ghost" size="icon" onClick={remove} data-testid="meeting-delete-btn"><Trash2 className="w-4 h-4 text-red-600" /></Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4" data-testid="meeting-header">
        <div className="space-y-1.5">
          <Label>Nombre del lead</Label>
          <Input value={m.lead_name || ''} onChange={(e) => change({ lead_name: e.target.value })} data-testid="meeting-lead-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Negocio / sector</Label>
          <Input value={m.negocio || ''} onChange={(e) => change({ negocio: e.target.value })} data-testid="meeting-business-input" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label>Fecha</Label>
            <Input type="date" value={m.fecha} onChange={(e) => e.target.value && change({ fecha: e.target.value })} data-testid="meeting-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Duración</Label>
            <Input value={m.duracion || ''} onChange={(e) => change({ duracion: e.target.value })} placeholder="45 min" data-testid="meeting-duration-input" />
          </div>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Asistentes por su parte</Label>
          <Input value={m.asistentes_lead} onChange={(e) => change({ asistentes_lead: e.target.value })} placeholder="Ej.: Laura (dueña), Pedro (socio)" data-testid="meeting-attendees-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Asistentes por la nuestra</Label>
          <div className="flex flex-wrap gap-2">
            {ceos.map((c) => (
              <button key={c.id} type="button" onClick={() => toggleCeo(c.id)} data-testid={`meeting-ceo-toggle-${c.id}`}
                className={`px-3 h-9 rounded-full border text-sm transition-colors ${m.ceo_ids.includes(c.id) ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      {(m.bloques || []).map((b, i) => (
        <MeetingBlock key={b.id} bloque={b} index={i} respuestas={m.respuestas || {}} steps={m.proximos_pasos}
          onAnswer={(id, v) => change({ respuestas: { ...(m.respuestas || {}), [id]: v } })}
          onSteps={(proximos_pasos) => change({ proximos_pasos })} />
      ))}
      {!(m.bloques || []).some((b) => b.proximos_pasos) && (
        <div className="space-y-1.5">
          <Label>Próximos pasos</Label>
          <NextStepsEditor steps={m.proximos_pasos} onChange={(proximos_pasos) => change({ proximos_pasos })} />
        </div>
      )}
      {!m.bloques && (
        <div className="space-y-1.5">
          <Label>Notas de la reunión</Label>
          <RichTextEditor value={m.notas_html} onChange={(notas_html) => change({ notas_html })} testId="meeting-notes-editor" minHeight={420} />
        </div>
      )}
    </div>
  );
};
